import { FC, FormEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { LoadingButton } from '@mui/lab'
import { Alert, Dialog, DialogActions, DialogContent, MenuItem, Stack, TextField } from '@mui/material'

import GRDialogTitle from '../../../../../components/GRDialogTitle/GRDialogTitle'
import { useQueueNewMetadataAnalysisMutation } from '../../../../api/ad-data-tester'
import { InternalMarket } from '../../../markets/types/InternalMarket'
import { isAppIdInvalid, parseAppId } from '../helpers/adDataHelpers'

type NewMetadataAnalysisDialogProps = {
  open: boolean
  markets: InternalMarket[]
  defaultMarketIso: string
  onClose: () => void
}

type Result = { severity: 'success' | 'error'; appId: number }

export const NewMetadataAnalysisDialog: FC<NewMetadataAnalysisDialogProps> = ({ open, markets, defaultMarketIso, onClose }) => {
  const { t } = useTranslation()
  const [queueNewMetadataAnalysis, { isLoading }] = useQueueNewMetadataAnalysisMutation()
  const [appIdText, setAppIdText] = useState('')
  const [marketIso, setMarketIso] = useState(defaultMarketIso)
  const [result, setResult] = useState<Result>()

  useEffect(() => {
    if (!open) return
    setAppIdText('')
    setMarketIso(defaultMarketIso)
    setResult(undefined)
  }, [open, defaultMarketIso])

  const appId = parseAppId(appIdText)
  const appIdInvalid = isAppIdInvalid(appIdText)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (appId === undefined || isLoading) return
    setResult(undefined)
    try {
      await queueNewMetadataAnalysis({ appId, market: marketIso }).unwrap()
      setResult({ severity: 'success', appId })
    } catch {
      setResult({ severity: 'error', appId })
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <form onSubmit={submit} noValidate>
        <GRDialogTitle onClose={onClose}>{t('internal-service-management:ad_data_new_analysis_title')}</GRDialogTitle>
        <DialogContent>
          <Stack spacing={2} pt={1}>
            <TextField
              select
              size="small"
              label={t('internal-common:market')}
              value={marketIso}
              onChange={(event) => setMarketIso(event.target.value)}
              disabled={isLoading}
            >
              {markets.map((market) => (
                <MenuItem key={market.iso} value={market.iso}>
                  {market.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label={t('internal-service-management:ad_data_app_id')}
              value={appIdText}
              onChange={(event) => setAppIdText(event.target.value)}
              disabled={isLoading}
              error={appIdInvalid}
              helperText={appIdInvalid ? t('internal-service-management:ad_data_app_id_invalid') : t('internal-service-management:ad_data_new_analysis_help')}
              inputProps={{ inputMode: 'numeric' }}
              autoFocus
            />
            {result && (
              <Alert severity={result.severity}>
                {result.severity === 'success'
                  ? t('internal-service-management:ad_data_new_analysis_queued', { appId: result.appId })
                  : t('internal-service-management:ad_data_new_analysis_failed', { appId: result.appId })}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <LoadingButton type="submit" variant="contained" loading={isLoading} disabled={appId === undefined}>
            {t('internal-service-management:ad_data_new_analysis_send')}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  )
}
